import { Button, Label, Spinner } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import FormIfStudying from './FormIfStudying';
import FormIfWorking from './FormIfWorking';
import FormIfGraduated from './FormIfGraduated';
import moment from 'moment';
import { useMutation, useQueryClient } from 'react-query';
import { HighestQualificationTypes, updateUserProfileDetails } from 'src/apis/user';
import { CurrentWorkingStatus, PIFFormData, PIFMeta, PreInterviewsFormData, UIUser } from 'types';
import { useMeetingContext } from 'src/context/MeetingProvider';

import { isUserCollegeUser } from 'shared-utils';
import { useUserContext } from 'src/context/UserProvider';
import { useNavigate } from 'react-router-dom';
import PersonalInfoForm from './PersonalInfoForm';
import ValidationErrorModal from './ValidationErrorModal';
import { useToast } from 'src/context/ToastProvider';
import { pifChangableFieldsConfig } from 'src/utils/pifConfig';

interface Props {
	allSkillsOptions?: {
		label: string;
		value: string;
	}[];
	fromEditProfile?: boolean;
}

const PreInterviewComponent = ({ fromEditProfile, allSkillsOptions }: Props) => {
	const queryClient = useQueryClient();
	const { meeting } = useMeetingContext();
	const { user: userData } = useUserContext();
	const user = fromEditProfile ? userData : userData;
	const navigate = useNavigate();

	const [userSkills, setUserSkills] = useState<{ rating: number; skill: string }[]>([]);
	const [showValidationError, setShowValidationError] = useState<string>();

	const [tab, setTab] = useState<CurrentWorkingStatus>();
	const [isValid, setIsValid] = useState<Boolean>(true);
	const [formData, setFormData] = useState<PIFFormData>({
		// PIF Related Data
		currentWorkingStatus: CurrentWorkingStatus.Studying,
		graduationYear: moment().year(),
		currentRole: '',
		currentCompany: '',
		currentSalary: undefined,
		expectedJoining: 'Immediate',
		preferredJobLocations: [],
		lastRole: '',
		lastCompany: '',
		lastDrawnSalary: 0,
		pifMeta: {
			gender: '',
			internshipExperience: '',
			totalExperience: '',
			relevantTechExperience: '',
			dateOfBirth: '',
			location: {
				state: '',
				city: '',
				pincode: '',
			},
			highestQualifications: '',
			tenth: {
				percentage: '',
				board: '',
			},
			twelfth: {
				percentage: '',
				board: '',
			},
			diplomaAfterTenth: {
				percentage: '',
				board: '',
				collegeUniversityName: '',
			},
			diplomaAfterTwelfth: {
				percentage: '',
				board: '',
				collegeUniversityName: '',
			},
			underGraduate: {
				percentage: '',
				collegeUniversityName: '',
				isBacklog: '',
				degree: '',
				specialisation: '',
			},
			postGraduate: {
				percentage: '',
				collegeUniversityName: '',
				isBacklog: '',
				degree: '',
				specialisation: '',
			},
			tenthMarksheetDoc: '',
			twelfthMarksheetDoc: '',
			highestQualificationMarksheetDoc: '',
			panCardDoc: '',
			aadhaarCardDocFront: '',
			aadhaarCardDocBack: '',
			guardianMobileNumber: '',
			primaryCommunicationPhoneNumber: '',
			primaryCommunicationEmail: '',
			linkedinUrl: '',
			githubProfileLink: '',
			leetcodeProfileLink: '',
			hackerRankProfileLink: '',
			facebookProfileLink: '',
			instagramProfileLink: '',
			clubsInvolvedInCollege: '',
			hobbies: '',
			achievements: '',
		},
	});

	// const resetFromData = () => {
	// 	setFormData({
	// 		expectedJoining: 'Immediate',
	// 		currentWorkingStatus: CurrentWorkingStatus.Studying,
	// 		expectedSalary: { min: 0, max: 0 },
	// 		lastDrawnSalary: 0,
	// 		lastCompany: '',
	// 		lastRole: '',
	// 		graduationYear: moment().year(),
	// 		preferredJobLocations: [],
	// 		// totalExperience: undefined,
	// 		// screeningCompletedBy: undefined,
	// 	});
	// 	setUserSkills([]);
	// };

	const checkIfDisabled = () => {
		if (tab === CurrentWorkingStatus.Studying) {
			return !formData.graduationYear ? true : false;
		} else if (tab === CurrentWorkingStatus.Working) {
			return (
				!formData.graduationYear ||
				!formData.currentRole ||
				!formData.currentCompany ||
				formData.currentSalary === undefined ||
				!formData.expectedJoining ||
				formData.preferredJobLocations?.length === 0 ||
				!isValid
			);
		} else if (tab === CurrentWorkingStatus.GraduatedNotWorking) {
			return (
				!formData.graduationYear ||
				formData.preferredJobLocations?.length === 0 ||
				// formData.lastDrawnSalary === undefined ||
				// formData.lastCompany?.length === 0 ||
				// formData.lastRole?.length === 0 ||
				!isValid
			);
		} else {
			return true;
		}
	};
	const toast = useToast();

	const updateProfileFn = useMutation(['updateUserProfileDetails'], updateUserProfileDetails, {
		onSuccess() {
			if (fromEditProfile) {
				toast({
					title: 'Profile updated successfully',
					type: 'success',
				});
				queryClient.refetchQueries(['verifyCookie']);
				// navigate('/dashboard');
				navigate('/my-applications');
			} else {
				queryClient.refetchQueries(['getMeetingById']);
			}
		},
	});

	const checkValidations = {
		[HighestQualificationTypes.Twelfth]: [
			{ value: formData?.pifMeta?.tenth?.percentage, message: 'Please provide 10th percentage' },
			{ value: formData?.pifMeta?.tenth?.board, message: 'Please provide 10th board name' },
			{ value: formData?.pifMeta?.twelfth?.percentage, message: 'Please provide 12th percentage' },
			{ value: formData?.pifMeta?.twelfth?.board, message: 'Please provide 12th board name' },
		],
		[HighestQualificationTypes.DiplomaAfterTenth]: [
			{ value: formData?.pifMeta?.tenth?.percentage, message: 'Please provide 10th percentage' },
			{ value: formData?.pifMeta?.tenth?.board, message: 'Please provide 10th board name' },
			{ value: formData?.pifMeta?.diplomaAfterTenth?.percentage, message: 'Please provide diploma percentage' },
			{ value: formData?.pifMeta?.diplomaAfterTenth?.board, message: 'Please provide diploma board name' },
			{
				value: formData?.pifMeta?.diplomaAfterTenth?.collegeUniversityName,
				message: 'Please provide college/university name',
			},
		],
		[HighestQualificationTypes.DiplomaAfterTwelfth]: [
			{ value: formData?.pifMeta?.tenth?.percentage, message: 'Please provide 10th percentage' },
			{ value: formData?.pifMeta?.tenth?.board, message: 'Please provide 10th board name' },
			{ value: formData?.pifMeta?.twelfth?.percentage, message: 'Please provide 12th percentage' },
			{ value: formData?.pifMeta?.twelfth?.board, message: 'Please provide 12th board name' },
			{ value: formData?.pifMeta?.diplomaAfterTwelfth?.percentage, message: 'Please provide diploma percentage' },
			{ value: formData?.pifMeta?.diplomaAfterTwelfth?.board, message: 'Please provide diploma board name' },
			{
				value: formData?.pifMeta?.diplomaAfterTwelfth?.collegeUniversityName,
				message: 'Please provide college/university name',
			},
		],
		[HighestQualificationTypes.UnderGraduate]: [
			{ value: formData?.pifMeta?.tenth?.percentage, message: 'Please provide 10th percentage' },
			{ value: formData?.pifMeta?.tenth?.board, message: 'Please provide 10th board name' },
			{ value: formData?.pifMeta?.twelfth?.percentage, message: 'Please provide 12th percentage' },
			{ value: formData?.pifMeta?.twelfth?.board, message: 'Please provide 12th board name' },
			{ value: formData?.pifMeta?.underGraduate?.percentage, message: 'Please provide undergraduate percentage' },
			{
				value: formData?.pifMeta?.underGraduate?.collegeUniversityName,
				message: 'Please provide college/university name',
			},
			{ value: formData?.pifMeta?.underGraduate?.isBacklog, message: 'Please provide backlog status' },
			{ value: formData?.pifMeta?.underGraduate?.degree, message: 'Please provide undergraduate degree' },
			{
				value: formData?.pifMeta?.underGraduate?.specialisation,
				message: 'Please provide undergraduate specialisation',
			},
		],
		[HighestQualificationTypes.PostGraduate]: [
			{ value: formData?.pifMeta?.tenth?.percentage, message: 'Please provide 10th percentage' },
			{ value: formData?.pifMeta?.tenth?.board, message: 'Please provide 10th board name' },
			{ value: formData?.pifMeta?.twelfth?.percentage, message: 'Please provide 12th percentage' },
			{ value: formData?.pifMeta?.twelfth?.board, message: 'Please provide 12th board name' },
			{ value: formData?.pifMeta?.underGraduate?.percentage, message: 'Please provide undergraduate percentage' },
			{
				value: formData?.pifMeta?.underGraduate?.collegeUniversityName,
				message: 'Please provide undergraduate college/university name',
			},
			{ value: formData?.pifMeta?.underGraduate?.isBacklog, message: 'Please provide undergraduate backlog status' },
			{ value: formData?.pifMeta?.underGraduate?.degree, message: 'Please provide undergraduate degree' },
			{
				value: formData?.pifMeta?.underGraduate?.specialisation,
				message: 'Please provide undergraduate specialisation',
			},
			{ value: formData?.pifMeta?.postGraduate?.percentage, message: 'Please provide postgraduate percentage' },
			{
				value: formData?.pifMeta?.postGraduate?.collegeUniversityName,
				message: 'Please provide postgraduate college/university name',
			},
			{ value: formData?.pifMeta?.postGraduate?.isBacklog, message: 'Please provide postgraduate backlog status' },
			{ value: formData?.pifMeta?.postGraduate?.degree, message: 'Please provide postgraduate degree' },
			{ value: formData?.pifMeta?.postGraduate?.specialisation, message: 'Please provide postgraduate specialisation' },
		],
	};

	const validatePifMetaData = (high: HighestQualificationTypes) => {
		const validations = checkValidations[high];

		if (validations) {
			for (let i = 0; i < validations.length; i++) {
				if (!validations[i].value && typeof validations[i].value !== 'boolean') {
					setShowValidationError(validations[i].message);
					return true;
				}
			}
		}

		return false;
	};

	const handleSubmit = () => {
		const phoneRegex = /^[0-9]{10}$/;
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

		if (isUserCollegeUser(user as unknown as UIUser) || user?.flags?.isMasaiStudent) {
			if (!formData?.pifMeta?.gender) {
				setShowValidationError('Please Select Your Gender');
				return;
			} else if (!formData?.pifMeta?.totalExperience) {
				setShowValidationError('Please Select Your Total Experience');
				return;
			} else if (!formData?.pifMeta?.relevantTechExperience) {
				setShowValidationError('Please Select Your Relevant Work Experience');
				return;
			} else if (!formData?.pifMeta?.dateOfBirth) {
				setShowValidationError('Please Select Your Date of Birth');
				return;
			} else if (!formData?.pifMeta?.location?.state) {
				setShowValidationError('Please Select Your State');
				return;
			} else if (!formData?.pifMeta?.location?.city) {
				setShowValidationError('Please Select Your City');
				return;
			} else if (!formData?.pifMeta?.location?.pincode) {
				setShowValidationError('Please Fill Your Pincode');
				return;
			} else if (!formData?.pifMeta?.highestQualifications) {
				setShowValidationError('Please Select Your Highest Qualifications');
				return;
			} else if (validatePifMetaData(formData?.pifMeta?.highestQualifications as HighestQualificationTypes)) return;
			//
			if (isUserCollegeUser(user as unknown as UIUser) || user?.flags?.isMasaiStudent) {
				// if (!formData?.pifMeta?.userPassportPhoto) {
				// 	setShowValidationError('Please upload Your Passport Photo');
				// 	return;
				// }
				// else
				if (!formData?.pifMeta?.tenthMarksheetDoc) {
					setShowValidationError('Please upload your 10th marksheet');
					return;
				} else if (!formData?.pifMeta?.twelfthMarksheetDoc) {
					setShowValidationError('Please upload your 12th marksheet');
					return;
				} else if (!formData?.pifMeta?.highestQualificationMarksheetDoc) {
					setShowValidationError('Please upload your highest qualification marksheet');
					return;
				} else if (!formData?.pifMeta?.panCardDoc) {
					setShowValidationError('Please upload your Pan Card');
					return;
				} else if (!formData?.pifMeta?.aadhaarCardDocFront) {
					setShowValidationError('Please upload your Aadhaar Card (front side)');
					return;
				} else if (!formData?.pifMeta?.aadhaarCardDocBack) {
					setShowValidationError('Please upload your Aadhaar Card (back side)');
					return;
				} else if (
					!formData?.pifMeta?.guardianMobileNumber ||
					!phoneRegex.test(formData?.pifMeta?.guardianMobileNumber) ||
					formData?.pifMeta?.guardianMobileNumber === userData?.phone
				) {
					setShowValidationError(
						`Please provide your Guardian 10 digit Mobile Number, it should be different than your own registered phone number: ${userData?.phone}`,
					);
					return;
				} else if (
					!formData?.pifMeta?.primaryCommunicationPhoneNumber ||
					!phoneRegex.test(formData?.pifMeta?.primaryCommunicationPhoneNumber)
				) {
					setShowValidationError('Please provide your Primary communication 10 digit Mobile Number');
					return;
				} else if (
					!formData?.pifMeta?.primaryCommunicationEmail ||
					!emailRegex.test(formData?.pifMeta?.primaryCommunicationEmail)
				) {
					setShowValidationError('Please provide your Primary communication email');
					return;
				} else if (
					!formData?.pifMeta?.primaryCommunicationEmail ||
					!emailRegex.test(formData?.pifMeta?.primaryCommunicationEmail)
				) {
					setShowValidationError('Please provide your Primary communication email');
					return;
				}
				if (isUserCollegeUser(user as unknown as UIUser)) {
					/*
					add check for:
						linkedinUrl
						githubProfileLink
						leetcodeProfileLink
						hackerRankProfileLink
						facebookProfileLink
						instagramProfileLink
						clubsInvolvedInCollege
						hobbies
						achievements
					*/
					if (!formData?.pifMeta?.linkedinUrl) {
						setShowValidationError('Please provide your linkedinUrl');
						return;
					} else if (!formData?.pifMeta?.githubProfileLink) {
						setShowValidationError('Please provide your githubProfileLink');
						return;
					} else if (!formData?.pifMeta?.leetcodeProfileLink) {
						setShowValidationError('Please provide your leetcodeProfileLink');
						return;
					} else if (!formData?.pifMeta?.hackerRankProfileLink) {
						setShowValidationError('Please provide your hackerRankProfileLink');
						return;
					} else if (!formData?.pifMeta?.facebookProfileLink) {
						setShowValidationError('Please provide your facebookProfileLink');
						return;
					} else if (!formData?.pifMeta?.instagramProfileLink) {
						setShowValidationError('Please provide your instagramProfileLink');
						return;
					} else if (!formData?.pifMeta?.clubsInvolvedInCollege) {
						setShowValidationError('Please provide your clubsInvolvedInCollege');
						return;
					} else if (!formData?.pifMeta?.hobbies) {
						setShowValidationError('Please provide your hobbies');
						return;
					} else if (!formData?.pifMeta?.achievements) {
						setShowValidationError('Please provide your achievements');
						return;
					}
				}
			}
		}

		if (userData || user) {
			updateProfileFn.mutate({
				id: user ? user?._id : '',
				data: { ...formData, currentWorkingStatus: tab, screenedSkills: userSkills } as PreInterviewsFormData,
			});
		}
	};

	useEffect(() => {
		if (!user) {
			return;
		}
		const userPifMeta = (user?.pifMeta || {}) as PIFMeta;
		console.log('userPifMeta:', userPifMeta);
		setTab(user.profile.currentWorkingStatus as CurrentWorkingStatus);
		setFormData({
			currentWorkingStatus: user.profile.currentWorkingStatus as CurrentWorkingStatus,
			graduationYear: user.profile.gradYear,
			currentRole: user.profile.currentRole,
			currentCompany: user.profile.currentCompany,
			currentSalary: user.profile.currentSalary,
			expectedJoining: user.profile.expectedJoining,
			preferredJobLocations: user.profile.preferredJobLocations || [],
			lastRole: user.profile.lastRole,
			lastCompany: user.profile.lastCompany,
			lastDrawnSalary: user.profile.lastDrawnSalary,
			// Part 2: Personal Data
			pifMeta: {
				gender: userPifMeta?.gender,
				internshipExperience: userPifMeta?.internshipExperience,
				totalExperience: userPifMeta?.totalExperience,
				relevantTechExperience: userPifMeta?.relevantTechExperience,
				dateOfBirth: userPifMeta?.dateOfBirth,
				location: {
					state: userPifMeta?.location?.state,
					city: userPifMeta?.location?.city,
					pincode: userPifMeta?.location?.pincode,
				},
				highestQualifications: userPifMeta?.highestQualifications,
				tenth: {
					percentage: userPifMeta?.tenth?.percentage,
					board: userPifMeta?.tenth?.board,
				},
				twelfth: {
					percentage: userPifMeta?.twelfth?.percentage,
					board: userPifMeta?.twelfth?.board,
				},
				diplomaAfterTenth: {
					percentage: userPifMeta?.diplomaAfterTenth?.percentage,
					board: userPifMeta?.diplomaAfterTenth?.board,
					collegeUniversityName: userPifMeta?.diplomaAfterTenth?.collegeUniversityName,
				},
				diplomaAfterTwelfth: {
					percentage: userPifMeta?.diplomaAfterTwelfth?.percentage,
					board: userPifMeta?.diplomaAfterTwelfth?.board,
					collegeUniversityName: userPifMeta?.diplomaAfterTwelfth?.collegeUniversityName,
				},
				underGraduate: {
					percentage: userPifMeta?.underGraduate?.percentage,
					collegeUniversityName: userPifMeta?.underGraduate?.collegeUniversityName,
					isBacklog: userPifMeta?.underGraduate?.isBacklog,
					degree: userPifMeta?.underGraduate?.degree,
					specialisation: userPifMeta?.underGraduate?.specialisation,
				},
				postGraduate: {
					percentage: userPifMeta?.postGraduate?.percentage,
					collegeUniversityName: userPifMeta?.postGraduate?.collegeUniversityName,
					isBacklog: userPifMeta?.postGraduate?.isBacklog,
					degree: userPifMeta?.postGraduate?.degree,
					specialisation: userPifMeta?.postGraduate?.specialisation,
				},
				tenthMarksheetDoc: userPifMeta?.tenthMarksheetDoc,
				twelfthMarksheetDoc: userPifMeta?.twelfthMarksheetDoc,
				highestQualificationMarksheetDoc: userPifMeta?.highestQualificationMarksheetDoc,
				panCardDoc: userPifMeta?.panCardDoc,
				aadhaarCardDocFront: userPifMeta?.aadhaarCardDocFront,
				aadhaarCardDocBack: userPifMeta?.aadhaarCardDocBack,
				guardianMobileNumber: userPifMeta?.guardianMobileNumber,
				primaryCommunicationPhoneNumber: userPifMeta?.primaryCommunicationPhoneNumber,
				primaryCommunicationEmail: userPifMeta?.primaryCommunicationEmail,
				linkedinUrl: userPifMeta?.linkedinUrl,
				githubProfileLink: userPifMeta?.githubProfileLink,
				leetcodeProfileLink: userPifMeta?.leetcodeProfileLink,
				hackerRankProfileLink: userPifMeta?.hackerRankProfileLink,
				facebookProfileLink: userPifMeta?.facebookProfileLink,
				instagramProfileLink: userPifMeta?.instagramProfileLink,
				clubsInvolvedInCollege: userPifMeta?.clubsInvolvedInCollege,
				hobbies: userPifMeta?.hobbies,
				achievements: userPifMeta?.achievements,
			},
		});
	}, [user]);

	// useEffect(() => {
	// 	resetFromData();
	// }, [tab]);

	// filter out skills that are already selected
	const filteredSkills = allSkillsOptions?.filter(
		skill => !userSkills.map(userSkill => userSkill.skill).includes(skill.value),
	);

	useEffect(() => {
		console.log('user phone:', user);
	}, [user]);
	return (
		<div className=" dark:bg-[#1F2A37] dark:text-[#FAFAFC] max-h-full min-h-full overflow-y-scroll">
			<form
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<div className="p-4 rounded-[8px] border border-solid border-[#c7c9d91a]">
					<p className="py-2 font-bold">
						{meeting?.isAIInterview
							? 'Before beginning the assessment, Please fill the following information'
							: fromEditProfile
							  ? 'Please fill the following Person Information form:'
							  : 'Before beginning the assessment, ask the interviewee to introduce themselves and fill the following information accordingly'}
					</p>
					<div>
						<Label className="dark:text-white">Current working status?</Label>
						<div className="flex gap-4 mt-2 font-inter">
							<div
								className={`rounded-[8px] w-max px-3 py-2 border border-[#7777E7] cursor-pointer flex justify-center items-center text-center ${
									tab === CurrentWorkingStatus.Studying
										? 'bg-[#7777E7] dark:bg-[#18222D] text-white dark:text-white'
										: 'bg-[#E5F0FF] text-black'
								}`}
								onClick={() => {
									if (user?.profile.currentWorkingStatus && pifChangableFieldsConfig.currentWorkingStatus === false) {
										return;
									}

									setFormData({
										...formData,
										currentWorkingStatus: CurrentWorkingStatus.Studying,
									});
									setTab(CurrentWorkingStatus.Studying);
								}}
							>
								Studying
							</div>
							<div
								className={`rounded-[8px] w-max px-3 py-2 border border-[#7777E7] cursor-pointer flex justify-center items-center text-center ${
									tab === CurrentWorkingStatus.Working
										? 'bg-[#7777E7] dark:bg-[#18222D] text-white dark:text-white'
										: 'bg-[#E5F0FF] text-black'
								}`}
								onClick={() => {
									if (user?.profile.currentWorkingStatus && pifChangableFieldsConfig.currentWorkingStatus === false) {
										return;
									}
									setFormData(prev => ({
										...formData,
										currentWorkingStatus: CurrentWorkingStatus.Working,
									}));
									setTab(CurrentWorkingStatus.Working);
								}}
							>
								Working
							</div>
							<div
								className={`rounded-[8px] w-max px-3 py-2 border border-[#7777E7] cursor-pointer flex justify-center items-center text-center ${
									tab === CurrentWorkingStatus.GraduatedNotWorking
										? 'bg-[#7777E7] dark:bg-[#18222D] text-white dark:text-white'
										: 'bg-[#E5F0FF] text-black'
								}`}
								onClick={() => {
									if (user?.profile.currentWorkingStatus && pifChangableFieldsConfig.currentWorkingStatus === false) {
										return;
									}
									setFormData({
										...formData,
										currentWorkingStatus: CurrentWorkingStatus.GraduatedNotWorking,
									});
									setTab(CurrentWorkingStatus.GraduatedNotWorking);
								}}
							>
								Graduated but not working
							</div>
						</div>
					</div>
					{tab === CurrentWorkingStatus.Studying ? (
						<FormIfStudying formData={formData} setFormData={setFormData} />
					) : tab === CurrentWorkingStatus.Working ? (
						<FormIfWorking formData={formData} setFormData={setFormData} isValid={isValid} setIsValid={setIsValid} />
					) : tab === CurrentWorkingStatus.GraduatedNotWorking ? (
						<FormIfGraduated formData={formData} setFormData={setFormData} isValid={isValid} setIsValid={setIsValid} />
					) : null}

					{fromEditProfile ||
					(tab && user && (isUserCollegeUser(user as unknown as UIUser) || user?.flags?.isMasaiStudent)) ? (
						<PersonalInfoForm formData={formData} setFormData={setFormData} />
					) : null}

					<div className="mt-4 sticky bottom-0 w-full dark:bg-transparent bg-[#f7f9fb] py-2">
						<Button color="purplePlaced" disabled={updateProfileFn.isLoading || checkIfDisabled()} type="submit">
							{updateProfileFn.isLoading ? <Spinner /> : 'Submit'}
						</Button>
					</div>
				</div>
			</form>

			<ValidationErrorModal
				isOpen={!!showValidationError}
				errorText={showValidationError || ''}
				onClose={() => setShowValidationError('')}
			/>
		</div>
	);
};

export default PreInterviewComponent;
